<template>
    <MonitorLayoutTemplate
        :table_title="table_title"
        :is_show_chart_dialog="is_show_chart_dialog"
    >
        <template #datetime>
            <QpeDatetime
                :props_date="date"
                :props_time="time"
                :name="default_value"
                @update-time="update_time"
                @change-parent-date-time="change_parent_date_time"
            />
        </template>
        
        <template #filter_condition_options>
            <MonitorOptionSet 
                ref="monitor_option_set"
                :option_set="option_set"
                @filter_data="filter_data"
            />
        </template>

        <template #data_table="slot_props">
            <QpeDatatable
                :data="table_data"
                :headers="header"
                :name="default_value"
                :error_text="error_text"
                :search="slot_props.search"
                :should_draw_diagram_header="should_draw_diagram_header"
                :enable_diagram_slot="true"
                :show_download_doc_btn="true"
                @update-time="update_time"
                @change-parent-date-time="change_parent_date_time"
                @open-chart="assign_chart_component"
                :is_show_chart_dialog_parent.sync="is_show_chart_dialog"
            >
                <template #diagram_dialog="{ close_dialog }"> 
                    <GisChart v-if="is_show_chart_dialog"
                        id="belongs_monitor_chart"
                        :chart_options="chart_options"
                        :chart_type="chart_options.type"
                        :default_variable="weather_history_option"
                        :prop_rain_selected="$t('__rainfall.hour_3')"
                        @close-chart-dialog="close_dialog"
                    />
                </template>
                <template #table_description>
                    <span>
                        說明："--"標示，顯示該欄位無資料。<br>
                        {{ $t('__monitor.min_10_gust_annotation') }}<br>
                        {{ $t('__monitor.hour_1_gust_annotation') }}<br>
                    </span>
                </template>
            </QpeDatatable>
        </template>
    </MonitorLayoutTemplate>
</template>

<script>
    import {  redirect_monitor_table_ajax, } from "@/utils/monitor/monitor.js"
    import { sort_callback_by_city_name_tw, add_gust_interval_label_callback } from '@/utils/monitor/common_tool.js'
    import {
        generate_variable_options,
        combine_new_variable_options,
        update_option_set_from_local_storage,
        update_current_option_value,
    } from '@/utils/monitor/option.js'
    import { get_stations_history_params } from "@/utils/chart/chart_ajax.js"
    import { monitor_mixin } from "@/utils/monitor/monitor_mixin.js"
    import { TABLE_VALUE_TEXT_MAPPING, WEATHER_HISTORY_OPTION_MAPPING } from "@/config/monitor_config.js"
    import { mapState } from "vuex"
    import MonitorLayoutTemplate from "@/components/monitor/_tool/layout_template.vue"
    import QpeDatetime from "@/components/monitor/qpe_datetime.vue"
    import MonitorOptionSet from "@/components/monitor/monitor_option_set.vue"
    import QpeDatatable from "@/components/monitor/qpe_datatable.vue"

    export default {
        name: 'SurfaceWeatherTHB',

        components: {
            MonitorLayoutTemplate,
            QpeDatetime,
            MonitorOptionSet, 
            QpeDatatable, 
            GisChart: () => import('@/components/chart/gis_chart.vue'),
        },

        mixins: [monitor_mixin],

        props: {
            activate_filter_by_parent: {
                type: Boolean,
                default: false
            },

            filter_value_from_parent: {
                type: String,
                default: ''
            }
        },

        data() {
            return {
                // 排序使用
                table_data: [],
                header: [],
                error_text: '',

                immutable_table_data: [],
                option_set: [],
                display_all_office: '所有養護工程分局',
                display_all_branch: '所有工務段',

                default_value: 13,

                should_draw_diagram_header: ['風向(degree)', '平均風(m/s)', '瞬間風(m/s)', '溫度(°C)', '相對濕度(%)', '氣壓(hPa)', '當日累積雨量(mm)'],
                should_draw_weather_diagram_header: ['風向(degree)', '平均風(m/s)', '瞬間風(m/s)', '溫度(°C)', '相對濕度(%)', '氣壓(hPa)'],
                should_draw_station_diagram_header: ['當日累積雨量(mm)'],

                is_show_chart_dialog: false,
                chart_options: {
                    type: { 'gauge': true, 'weather': true }
                },
                weather_history_option: '',

                local_storage_option_name: 'surface_weather_option',
            }
        },

        computed: {
            ...mapState([
                'client_name',
            ]),

            table_title() {
                return `${TABLE_VALUE_TEXT_MAPPING[this.default_value]['tag_name']}資訊`
            }
        },
        
        watch: {
            immutable_table_data: {
                handler() {
                    if (Object.keys(this.immutable_table_data).length === 0) return
                    
                    this.generate_option_set()
                    this.option_set = update_option_set_from_local_storage(this.option_set , this.local_storage_option_name)
                },
                deep: true
            },
        },
        
        created() {
            redirect_monitor_table_ajax.call(this, this.default_value)
        },

        methods: {
            generate_table_data(data, header) {
                const has_gust_interval_label_data = data
                    .sort(sort_callback_by_city_name_tw)
                    .map(add_gust_interval_label_callback('瞬間風(m/s)'))
                this.immutable_table_data = has_gust_interval_label_data;
                this.table_data = has_gust_interval_label_data;
                this.header = header;
            },

            generate_option_set() {
                const basic_variable_options = [
                    { default_value: this.display_all_office, json_key:'所屬單位', label: 'office' },
                    { default_value: this.display_all_branch, json_key:'路段', label: 'branch' },
                ]

                const variable_options = generate_variable_options(basic_variable_options, this.immutable_table_data)
                
                if (this.option_set.length === 0) {    
                    this.option_set = [...variable_options]
                } else {
                    this.option_set = combine_new_variable_options(this.option_set, variable_options)
                    this.$refs.monitor_option_set.change_option_set()
                }
            },

            filter_data(filtered_cond, change_condition_label) {
                const update_current_value_by_filtered_cond = update_current_option_value(filtered_cond)
                this.option_set = this.option_set.map(update_current_value_by_filtered_cond)

                if (change_condition_label === 'office') {
                    this.option_set[1].current_option_value = this.display_all_branch
                    filtered_cond['branch'] = this.display_all_branch
                }

                const compare_office = filtered_cond['office'] === this.display_all_office ? '' : filtered_cond['office']
                const compare_branch = filtered_cond['branch'] === this.display_all_branch ? '' : filtered_cond['branch']
        
                const filtered_data = this.immutable_table_data.filter((item) => {
                    // 原始資料 雨量 mm 顯示 -- 處理方式為改為 0 後做比較，建立新變數以避免改到原值
                    return (
                        item['所屬單位'].includes(compare_office) &&
                        item['路段'].includes(compare_branch)
                    )
                })
                this.table_data = filtered_data

                this.generate_branch_option(filtered_cond['office'])
                
                // 把目前選擇的 option 存起來
                localStorage.setItem('surface_weather_option', JSON.stringify(filtered_cond))
            },

            generate_branch_option(office) {
                const filter_towns_by_office = item => item['所屬單位'] === office
                const get_branch = item => item['路段']
                const filtered_towns_options = office === this.display_all_office ? this.immutable_table_data : this.immutable_table_data.filter(filter_towns_by_office)
                const towns_options = filtered_towns_options.map(get_branch)
                this.option_set[1].option_array = [this.display_all_branch, ...new Set(towns_options)]
            },

            assign_chart_component(header_name, choosed_value, current_row_data) {
                this.chart_options = { ...this.chart_options, ...get_stations_history_params.call(this, current_row_data) }

                const special_weather_history_option = WEATHER_HISTORY_OPTION_MAPPING[header_name]
                if (special_weather_history_option) {
                    return this.weather_history_option = special_weather_history_option
                } 
                    
                this.weather_history_option = header_name.split('(')[0].trim()
            }
            
        },
    }
</script>